import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/AdminFilmographyTab.css';

const API_BASE = process.env.REACT_APP_API_BASE || '';

export default function AdminFilmographyTab() {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState('');

  // ✅ 초기 폼 데이터에 모든 언어 컬럼 추가
  const initialForm = { 
    category: 'DRAMA', year: '', 
    title_kr: '', title_en: '', title_jp: '', title_ch: '', 
    role_kr: '', role_en: '', role_jp: '', role_ch: '',
    sort_order: 0, media_type: 'NONE', media_url: '', is_visible: 1
  };
  
  const [f, setF] = useState(initialForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  function getYoutubeId(url) {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }

  async function load() {
    try {
      const res = await fetch(`${API_BASE}/api/admin_filmography_list.php`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { setMsg(data?.error || 'Failed to load'); return; }
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { setMsg('서버 연결 오류'); }
  }

  useEffect(() => { load(); }, []);

  const showMsg = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(''), 3000);
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setF(initialForm);
    setSelectedFile(null);
    setShowModal(true);
  };

  const startEdit = (item) => {
    setIsEditMode(true);
    setF({ ...item }); // DB에서 넘어온 title_kr, title_en 등이 모두 포함됨
    setSelectedFile(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setF(initialForm);
    setSelectedFile(null);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (selectedFile && selectedFile.size > 2 * 1024 * 1024) {
        return showMsg('파일 용량이 너무 큽니다. (2MB 제한)');
    }

    const formData = new FormData();
    // ✅ 모든 필드를 FormData에 추가
    Object.keys(f).forEach(key => formData.append(key, f[key] ?? ''));
    if (selectedFile) formData.append('image_file', selectedFile);

    const url = isEditMode 
      ? `${API_BASE}/api/admin_filmography_update.php` 
      : `${API_BASE}/api/admin_filmography_create.php`;

    const res = await fetch(url, {
      method: 'POST', credentials: 'include', body: formData
    });
    const data = await res.json();
    
    if (!res.ok) return showMsg(data.error || '저장 실패');
    
    showMsg(isEditMode ? 'Updated ✅' : 'Saved ✅');
    closeModal();
    load();
  }

  async function del(id) {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    const res = await fetch(`${API_BASE}/api/admin_filmography_delete.php`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ id })
    });
    if (!res.ok) return showMsg('삭제 실패');
    showMsg('Deleted ✅');
    load();
  }

  const grouped = useMemo(() => {
    const categories = ['DRAMA', 'FEATURE FILM', 'SHORT FILM', 'PLAY', 'COMMERCIALS', 'MUSIC VIDEO', 'AWARDS'];
    const res = {};
    categories.forEach(c => {
      res[c] = items.filter(it => it.category === c).sort((a, b) => {
        if (b.year !== a.year) {
          return b.year - a.year;
        }
        return b.sort_order - a.sort_order;
      });
    });
    return res;
  }, [items]);

  const categoryOptions = ["DRAMA", "FEATURE FILM", "SHORT FILM", "PLAY", "COMMERCIALS", "MUSIC VIDEO", "AWARDS"];

  return (
    <div className="admin-tab-content">
      {msg && <div className={`admin-toast ${msg.includes('✅') ? 'success' : 'error'}`}>{msg}</div>}

      <div className="admin-list-header">
          <button onClick={openCreateModal} className="admin-btn primary">+ NEW FILMOGRAPHY</button>
      </div>

      <div className="admin-grid single-col">
        <main className="admin-main-list">
          <section className="admin-card">
            <div className="card-head">
              <h3>DATABASE</h3>
              <span className="card-dot"></span>
            </div>
            <div className="admin-list-container">
              {Object.entries(grouped).map(([cat, list]) => (
                <div key={cat} className="list-category-group" style={{marginBottom: 35}}>
                  <h4 className="list-cat-title">{cat} <span className="cat-count">{list.length}</span></h4>
                  <div className="list-items">
                    {list.map(it => (
                      <div key={it.id} className="admin-item-row compact">
                        <div className="item-main-grid">
                          <div className="item-thumb-box">
                            {/* 1. 이미지 타입일 때 */}
                            {it.media_type === 'IMAGE' && it.media_url ? (
                              <img src={`${API_BASE}${it.media_url}`} alt="poster" className="item-thumb" />
                            ) 
                            /* 2. 비디오 타입일 때 (유튜브 썸네일 추출) */
                            : it.media_type === 'VIDEO' && it.media_url ? (
                              <div className="item-thumb-video-wrapper">
                                {it.media_url.includes('tiktok.com') ? (
                                  <img 
                                    src="/assets/tiktok.jpg"  /* 또는 경로에 따라 /assets/tiktok.jpg 등 */
                                    alt="tiktok thumb" 
                                    className="item-thumb" 
                                    style={{ objectFit: 'cover' }}
                                  />
                                ) : (
                                <img 
                                  src={`https://img.youtube.com/vi/${getYoutubeId(it.media_url)}/mqdefault.jpg`} 
                                  alt="video thumb" 
                                  className="item-thumb" 
                                  onError={(e) => { e.target.style.display = 'none'; }} // 유효하지 않은 URL일 경우 대비
                                />
                                )}
                                <div className="thumb-play-overlay">▶</div>
                              </div>
                            ) 
                            /* 3. 데이터가 없을 때 */
                            : (
                              <div className="item-thumb-empty">{cat[0]}</div>
                            )}
                          </div>
                          <div className="item-body">
                            <div className="item-header-meta">
                              <span className="item-year">{it.year}</span>
                              <span className="item-type-badge">{it.media_type}</span>
                              <span className="item-type-badge" style={{ opacity: Number(it.is_visible) === 1 ? 1 : 0.55 }}>
                                {Number(it.is_visible) === 1 ? 'VISIBLE' : 'HIDDEN'}
                              </span>
                            </div>
                            {/* ✅ 목록에서도 한국어 제목/역할을 기본으로 노출 */}
                            <p className="item-title">{it.title_kr}</p>
                            <p className="item-role">{it.role_kr}</p>
                          </div>
                          <div className="item-actions-side">
                            <button onClick={() => startEdit(it)} className="action-btn edit">EDIT</button>
                            <button onClick={() => del(it.id)} className="action-btn del">DEL</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* --- 통합 등록/수정 모달 --- */}
      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-card modal-card" style={{maxWidth: '750px', width: '95%', maxHeight: '80vh', overflowY: 'auto'}}>
            <div className="card-head">
              <h3>{isEditMode ? 'EDIT FILMOGRAPHY' : 'NEW FILMOGRAPHY'}</h3>
              <button onClick={closeModal} className="close-btn">&times;</button>
            </div>
            
            <form onSubmit={handleSubmit} className="admin-form">
              {/* 기본 설정 섹션 */}
              <div className="form-section-title">BASIC INFO</div>
              <div className="input-row">
                <div className="input-group">
                  <label>Category</label>
                  <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>
                    {categoryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="input-group">
                  <label>Year</label>
                  <input value={f.year} onChange={e => setF({ ...f, year: e.target.value })} placeholder="2025" required />
                </div>
                <div className="input-group">
                  <label>Order</label>
                  <input type="number" value={f.sort_order} onChange={e => setF({ ...f, sort_order: Number(e.target.value) })} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={Number(f.is_visible) === 1}
                    onChange={e => setF({ ...f, is_visible: e.target.checked ? 1 : 0 })}
                  />
                  VIEW
                </label>
              </div>

              {/* 다국어 제목 섹션 */}
              <div className="form-section-title">TITLES (BY LANGUAGE)</div>
              <div className="input-row">
                <div className="input-group">
                  <label>Title (KR)</label>
                  <input value={f.title_kr} onChange={e => setF({ ...f, title_kr: e.target.value })} placeholder="작품명" required />
                </div>
                <div className="input-group">
                  <label>Title (EN)</label>
                  <input value={f.title_en} onChange={e => setF({ ...f, title_en: e.target.value })} placeholder="English Title" />
                </div>
              </div>
              <div className="input-row">
                <div className="input-group">
                  <label>Title (JP)</label>
                  <input value={f.title_jp} onChange={e => setF({ ...f, title_jp: e.target.value })} placeholder="日本語題名" />
                </div>
                <div className="input-group">
                  <label>Title (CH)</label>
                  <input value={f.title_ch} onChange={e => setF({ ...f, title_ch: e.target.value })} placeholder="中文书名" />
                </div>
              </div>

              {/* 다국어 역할 섹션 */}
              <div className="form-section-title">ROLES (BY LANGUAGE)</div>
              <div className="input-row">
                <div className="input-group">
                  <label>Role (KR)</label>
                  <input value={f.role_kr} onChange={e => setF({ ...f, role_kr: e.target.value })} placeholder="역할명" required />
                </div>
                <div className="input-group">
                  <label>Role (EN)</label>
                  <input value={f.role_en} onChange={e => setF({ ...f, role_en: e.target.value })} placeholder="Character Name" />
                </div>
              </div>
              <div className="input-row">
                <div className="input-group">
                  <label>Role (JP)</label>
                  <input value={f.role_jp} onChange={e => setF({ ...f, role_jp: e.target.value })} placeholder="配役" />
                </div>
                <div className="input-group">
                  <label>Role (CH)</label>
                  <input value={f.role_ch} onChange={e => setF({ ...f, role_ch: e.target.value })} placeholder="角色" />
                </div>
              </div>

              {/* 미디어 섹션 */}
              <div className="form-section-title">MEDIA SETTINGS</div>
              <div className="input-group">
                <label>Media Type</label>
                <select value={f.media_type} onChange={e => setF({ ...f, media_type: e.target.value })}>
                  <option value="NONE">NONE</option>
                  <option value="IMAGE">IMAGE</option>
                  <option value="VIDEO">VIDEO</option>
                </select>
              </div>

              {f.media_type === 'IMAGE' && (
                <div className="input-group">
                  <label>{isEditMode ? 'Replace Poster' : 'Poster'}</label>
                  <input type="file" onChange={e => setSelectedFile(e.target.files[0])} accept="image/*" />
                </div>
              )}
              
              {f.media_type === 'VIDEO' && (
                <div className="input-group">
                  <label>Video URL</label>
                  <input value={f.media_url} onChange={e => setF({ ...f, media_url: e.target.value })} placeholder="https://..." />
                </div>
              )}

              <div className="form-actions" style={{marginTop: '30px', display: 'flex', gap: '10px'}}>
                <button type="submit" className="admin-btn primary" style={{flex: 1}}>{isEditMode ? 'SAVE CHANGES' : 'PUBLISH'}</button>
                <button type="button" onClick={closeModal} className="admin-btn secondary" style={{flex: 1}}>CANCEL</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
